<template>
  <div :class="'m-2' + (dev_background ? 'dev_background' : '')">
    <HandleCall />
    <Toast />
    <ConfirmPopup />
    <Menubar :model="items" v-if="isSuccess">
      <template #end>
        <div class="flex flex-wrap gap-2 items-center">
          <Button
            :icon="dark_theme ? 'pi pi-sun' : 'pi pi-moon'"
            severity="contrast"
            @click="toggle_color_scheme()"
            class="mr-1"
            size="small"
            v-tooltip.bottom="$t('Dark mode')"
          />
          <Select
            v-model="$i18n.locale"
            :options="$i18n.availableLocales"
            size="small"
          ></Select>
          <Avatar :label="username[0]" @click="toggle_avatar"></Avatar>
        </div>
        <Menu ref="avatar_menu" :model="avatar_items" popup></Menu>
      </template>
    </Menubar>
    <template v-if="isRefreshing || reconnexion">
      <h1>
        {{ $t("Token expiré, demande un refresh token") }}
      </h1>
      <ProgressSpinner style="width: 50px; height: 50px"></ProgressSpinner>
    </template>
    <!-- <div v-if="errorStore.error"> -->
    <!-- çà met le bazaaar, on verra plus tard... -->
    <div v-else-if="false">
      <DashboardBackEndNotFound></DashboardBackEndNotFound>
    </div>
    <div v-else>
      <router-view />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type GlobalComponents } from 'vue';

import { mapStores } from 'pinia';
import useErrorStore from './stores/errorStore';
import { useUser } from './composables/useUser';
import { useCall } from './composables/useCall';
import userManager from './auth/oidc';
import axios from 'axios';
import { DateTime } from 'luxon';
import router from './router';
import DashboardBackEndNotFound from './views/DashboardBackEndNotFound.vue';
import HandleCall from './components/HandleCall.vue';
import { syncAuthWithServiceWorker, registerQueueReplayTriggers } from './auth/serviceWorkerAuth';
import {version} from '../package.json'


let sw_registered = false
export default defineComponent({
  components: {DashboardBackEndNotFound, HandleCall},
  setup(){
    const { getUser, signoutRedirect, refreshToken, data: user, isRefreshing, isSuccess, has_permission} = useUser()
    const { initDevice } = useCall()

    const dev_background = import.meta.env.VITE_ENV == "dev"
     return { getUser, signoutRedirect, refreshToken, user, isRefreshing, isSuccess, has_permission,
      dev_background,
      version,
      initDevice
     }

  },
  data() {
    return {
      items: [
        {
          label: this.$t('Accueil'),
          icon: 'pi pi-home',
          command: () => this.$router.push('/')
        },
      ],
      avatar_items: [
        {
          label : `${this.$t('Version')}: ${this.version}`,
          icon: 'far fa-file-code',
        },
        {
          label: this.$t('Déconnexion'),
          icon: 'fas fa-sign-out-alt',
          command: () => this.signoutRedirect()
        }
      ],
      reconnexion: false,
      dark_theme: false,
    }
  },
  beforeMount() {
    this.get_color_scheme()
    this.apply_color_scheme()

  },
  async mounted() {
    router.beforeEach(async (to, from) => {
      if (to.meta?.requiresAuth === false) {
        return true
      }
      await this.getUser()
      try {
        return await this.checkuser()
      } catch (error) {
        console.error(error)
        console.warn("echec du checkuser, on redirige vers la connexion")
        userManager.signinRedirect()
        const errorStore = useErrorStore()
        errorStore.error = true
      }
    })

  },
  methods: {
    async checkuser(resign = false): Promise<boolean|undefined> {
      // console.log('checkuser user:', user !== null, ', resign:', resign)
      if (!this.user) {
        console.log('Redirection vers la page de connexion')

        // Si backend indisponible et pas de user, le signinRedirect juste après va recharger en boucle la page
        // Ce appel est juste un ping pour le backend!
        const backend_url = userManager.settings.authority
        await axios.get(backend_url)

        userManager.signinRedirect()
      } else if (this.user.expired) {
        if (resign) {
          console.warn('User expiré 2 fois de suite. Est ce possible???')
          return false
        }
        const date_expir = DateTime.fromSeconds(this.user.expires_at ?? 0).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)
        console.log('Token expiré, demande un refresh token... ', date_expir)
        this.reconnexion = true
        try {
          await this.refreshToken()
        } catch (error:unknown) {
          this.reconnexion = false
          // Si l'erreur concerne auth_time, forcer une nouvelle authentification
          if (error instanceof Error && error.message?.includes('auth_time')) {
            //L'erreur est surement "auth_time in id_token does not match original auth_time"
            // Peut être y a t il un meilleur moyen de gérer çà. Mais au moins çà fait le taf
            console.warn('Erreur auth_time lors du refresh, redirection vers connexion complète', error)
            userManager.signinRedirect()
            return false
          }
          // Relancer l'erreur pour les autres cas
          throw error
        }
        this.reconnexion = false
        return await this.checkuser(true)
      } else {
        this.initDevice()

        if (!sw_registered) {
          sw_registered = true
          navigator.serviceWorker.register('/sw.js', { scope: '/' }).then(async () => {
            // Attendre que le service worker soit actif et contrôle la page
            await navigator.serviceWorker.ready

            // Si le service worker vient d'être installé, attendre qu'il contrôle la page
            if (!navigator.serviceWorker.controller) {
              // Attendre l'événement controllerchange
              await new Promise<void>((resolve) => {
                navigator.serviceWorker.addEventListener(
                  'controllerchange',
                  () => {
                    console.log('Service Worker prend le contrôle de la page')
                    resolve()
                  },
                  { once: true },
                )
              })
            }

            // Maintenant le service worker est prêt, on peut synchroniser l'auth
            console.log("Synchronisation de l'authentification avec le Service Worker")
            syncAuthWithServiceWorker(this.user ?? undefined)
            registerQueueReplayTriggers()
          })
        }

        return true
      }
    },
    get_color_scheme(){
      const theme = localStorage.getItem('theme');
      if (theme) {
        if (theme === 'theme-dark') {
          this.dark_theme = true
        }
        if (theme === 'theme-light') {
          this.dark_theme = false
        }
        return
      }

      const prefersDarkTheme = window.matchMedia('(prefers-color-scheme: dark)');
      if (prefersDarkTheme.matches) {
        this.dark_theme = true
        return;
      }
      this.dark_theme = false

    },
    apply_color_scheme(){
      if (this.dark_theme){
        document.documentElement.classList.add('my-app-dark');
      } else {
        document.documentElement.classList.remove('my-app-dark');
      }
    },
    toggle_color_scheme() {
        this.dark_theme = ! this.dark_theme
        document.documentElement.classList.toggle('my-app-dark');
        localStorage.setItem('theme', this.dark_theme ? 'theme-dark': 'theme-light');
    },
    toggle_avatar(event:Event) {
      const menu_ref = this.$refs.avatar_menu as InstanceType<GlobalComponents["Menu"]>
      menu_ref.toggle(event)
    },
  },
  computed: {
    ...mapStores(useErrorStore),

    username(){
      const profile = this.user?.profile
      return profile?.name || profile?.preferred_username || '';
    }
  },

})
</script>
